import { SectionShell, Field } from "./SectionShell";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Checkbox } from "../ui/checkbox";
import { APPROACHES, SIGNALS, STRENGTH, inputCls } from "./constants";

export default function ReadinessTab({ data, update }) {
  const setR = (key, value) => update({ readiness: { ...data.readiness, [key]: value } });
  return (
    <SectionShell eyebrow="Section 5 · 5 min" title="Implementation Readiness">
      <div className="space-y-6">
        <Field label={`Tech Comfort Level (1-5): ${data.readiness?.tech_comfort ?? 3}`}>
          <input type="range" min={1} max={5} data-testid="tech-comfort"
            value={data.readiness?.tech_comfort ?? 3}
            onChange={(e) => setR("tech_comfort", parseInt(e.target.value))}
            className="w-full accent-[#C69F56]" />
        </Field>
        <Field label="Who would set up and maintain new tools?">
          <Input data-testid="who-sets-up" className={inputCls} value={data.readiness?.who_sets_up || ""} onChange={(e) => setR("who_sets_up", e.target.value)} />
        </Field>
        <Field label="Past experience implementing new tools?">
          <Textarea data-testid="past-exp" className={inputCls} rows={2} value={data.readiness?.past_experience || ""} onChange={(e) => setR("past_experience", e.target.value)} />
        </Field>
        <Field label="Time available for implementation in the next 2 weeks?">
          <Input data-testid="avail-time" className={inputCls} value={data.readiness?.available_time || ""} onChange={(e) => setR("available_time", e.target.value)} />
        </Field>
        <Field label="Preferred Approach">
          <RadioGroup value={data.readiness?.preferred_approach || ""} onValueChange={(v) => setR("preferred_approach", v)} className="grid md:grid-cols-3 gap-3">
            {APPROACHES.map((a) => (
              <label key={a} className="flex items-center gap-2 bg-ivory border border-navy/10 p-3 cursor-pointer hover:border-gold">
                <RadioGroupItem value={a} id={`ap-${a}`} data-testid={`approach-${a}`} /> <span className="text-sm text-navy">{a}</span>
              </label>
            ))}
          </RadioGroup>
        </Field>

        <div className="pt-6 border-t border-navy/10">
          <div className="eyebrow text-gold mb-2">Team & Operations</div>
          <p className="text-xs text-navy/60 mb-5">Feeds the Audit Snapshot (team/accountability gaps) and Internal Prep Sheet.</p>
          <div className="space-y-5">
            <Field label="Team roles — who owns what?">
              <Textarea data-testid="readiness-team-roles" className={inputCls} rows={3} value={data.readiness?.team_roles || ""}
                onChange={(e) => setR("team_roles", e.target.value)}
                placeholder="Name + role + main responsibility. Note any role gaps or owner-dependency." />
            </Field>
            <Field label="What's documented vs tribal knowledge?">
              <Textarea data-testid="readiness-sops" className={inputCls} rows={2} value={data.readiness?.existing_sops || ""}
                onChange={(e) => setR("existing_sops", e.target.value)}
                placeholder="Existing SOPs, Notion/Confluence pages, training docs — or in someone's head." />
            </Field>
            <Field label="What can you see (and not see) in your business today?">
              <Textarea data-testid="readiness-reporting" className={inputCls} rows={2} value={data.readiness?.reporting_visibility || ""}
                onChange={(e) => setR("reporting_visibility", e.target.value)}
                placeholder="Dashboards, reports, gut-feel — what visibility do you have on revenue, pipeline, ops?" />
            </Field>
          </div>
        </div>

        <Field label="Upsell Signals Detected">
          <div className="space-y-2">
            {SIGNALS.map((sig) => {
              const checked = (data.readiness?.upsell_signals || []).includes(sig);
              return (
                <label key={sig} className="flex items-center gap-3 bg-ivory border border-navy/10 p-3 cursor-pointer">
                  <Checkbox checked={checked} data-testid={`signal-${sig.slice(0, 10)}`}
                    onCheckedChange={(c) => {
                      const curr = data.readiness?.upsell_signals || [];
                      const next = c ? [...curr, sig] : curr.filter((s) => s !== sig);
                      setR("upsell_signals", next);
                    }} />
                  <span className="text-sm text-navy">{sig}</span>
                </label>
              );
            })}
          </div>
        </Field>
        <Field label="Signal Strength">
          <RadioGroup value={data.readiness?.signal_strength || ""} onValueChange={(v) => setR("signal_strength", v)} className="flex gap-4">
            {STRENGTH.map((s) => (
              <label key={s} className="flex items-center gap-2 text-sm text-navy">
                <RadioGroupItem value={s} id={`ss-${s}`} data-testid={`strength-${s}`} /> {s}
              </label>
            ))}
          </RadioGroup>
        </Field>
      </div>
    </SectionShell>
  );
}
